import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { del, get, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { dateTime, display, humanize } from '@/lib/utils';
import { ConfirmDialog, DataTable, FilterBar, FilterField, PageHeader, Tabs } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { scheduleAccess } from './schedule-access';
import { CLINIC_HOURS_PAGE, weekSummary, weekdayLabel, type ClinicHoursRow } from './clinic-hours-model';

interface BlockRow {
  id: string;
  doctorId: string;
  startsAt: string;
  endsAt: string;
  reason: string;
  notes: string | null;
}

export function SchedulesPage() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = useWorkingBranchId();

  const [tab, setTab] = useState<'templates' | 'leave'>('templates');
  const [doctorId, setDoctorId] = useState('');
  const [removingBlock, setRemovingBlock] = useState<string | null>(null);
  const [removingHours, setRemovingHours] = useState<ClinicHoursRow | null>(null);

  const access = scheduleAccess({ all: can('schedule.manage'), own: can('schedule.manage.own') }, user?.id ?? null);
  const manage = access.involved;

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => (await get<{ data: { id: string; name: string }[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });

  const schedules = useInfiniteQuery({
    queryKey: ['schedules', doctorId, branchId],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<ClinicHoursRow[]>(`/schedules${qs({ doctorId: doctorId || undefined, branchId: branchId || undefined, limit: CLINIC_HOURS_PAGE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => (last.length === CLINIC_HOURS_PAGE ? last[last.length - 1].id : undefined),
  });
  const rows = schedules.data?.pages.flat();

  const blocks = useQuery({
    queryKey: ['schedule-blocks', doctorId],
    queryFn: () => get<BlockRow[]>(`/schedules/blocks${qs({ doctorId: doctorId || undefined })}`),
    enabled: tab === 'leave',
  });

  const removeBlock = useMutation({
    mutationFn: (id: string) => del(`/schedules/blocks/${id}`),
    onSuccess: () => {
      toast.success(tr('opd', 'leave_removed'));
      void queryClient.invalidateQueries({ queryKey: ['schedule-blocks'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_remove_the_leave'), describeError(error)),
  });

  const removeSchedule = useMutation({
    mutationFn: (id: string) => del(`/schedules/${id}`),
    onSuccess: () => {
      toast.success(tr('opd', 'clinic_hours_removed'));
      void queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_remove_this_schedule'), describeError(error)),
  });

  const doctorName = (id: string) => doctors.data?.find((d) => d.id === id)?.name ?? '—';
  const dayLabel = (weekday: number) => weekdayLabel(weekday, display.locale, 'short');

  return (
    <>
      <PageHeader
        title={tr('opd', 'clinic_hours')}
        description={tr('opd', 'weekly_templates_that_generate_the_bookable')}
        actions={
          manage && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/schedules/leave/new')}>
                {tr('opd', 'mark_leave')}
              </Button>
              <Button onClick={() => navigate('/schedules/new')}>{tr('opd', 'publish_schedule')}</Button>
            </div>
          )
        }
      />

      <Tabs
        tabs={[
          { id: 'templates', label: tr('opd', 'weekly_hours') },
          { id: 'leave', label: tr('opd', 'leave_and_blocks') },
        ]}
        active={tab}
        onChange={(id) => setTab(id as 'templates' | 'leave')}
      />

      <FilterBar>
        <FilterField label={tr('opd', 'doctor')}>
          <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="w-64">
            <option value="">{tr('opd', 'all_doctors')}</option>
            {doctors.data?.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      {tab === 'templates' ? (
        <>
          <DataTable
            rows={rows}
            loading={schedules.isLoading}
            refreshing={schedules.isFetching && !schedules.isFetchingNextPage}
            partial={Boolean(schedules.hasNextPage)}
            error={schedules.error}
            rowKey={(row) => row.id}
            empty={tr('opd', 'no_clinic_hours_published_yet_until_a_doctor')}
            columns={[
              {
                header: tr('opd', 'doctor'),
                sort: (row) => `${row.doctor.name} ${row.department?.name ?? ''}`,
                cell: (row) => (
                  <div>
                    <p className="font-medium">
                      {row.doctor.name} — {row.department?.name ?? tr('opd', 'all_departments')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {row.department ? (row.doctor.designation ?? tr('opd', 'doctor')) : tr('opd', 'no_department_pick_when_editing')}
                    </p>
                  </div>
                ),
              },
              {
                header: tr('opd', 'weekly_hours'),
                cell: (row) => {
                  const lines = weekSummary(row.slots, dayLabel);
                  return lines.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="space-y-0.5 text-sm tabular">
                      {lines.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  );
                },
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  access.mayManageFor(row.doctorId) ? (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/schedules/${row.id}/edit`)}>
                        {tr('opd', 'edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={tr('opd', 'remove_clinic_hours')}
                        title={tr('opd', 'remove_clinic_hours')}
                        onClick={() => setRemovingHours(row)}
                        loading={removeSchedule.isPending && removeSchedule.variables === row.id}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
          {schedules.hasNextPage && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => void schedules.fetchNextPage()} loading={schedules.isFetchingNextPage}>
                {tr('opd', 'load_more')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <DataTable
          rows={blocks.data}
          loading={blocks.isLoading}
          refreshing={blocks.isFetching}
          error={blocks.error}
          rowKey={(row) => row.id}
          empty={tr('opd', 'no_leave_recorded_slots_inside_a_leave_block')}
          columns={[
            { header: tr('opd', 'doctor'), sort: (row) => doctorName(row.doctorId), cell: (row) => doctorName(row.doctorId) },
            { header: tr('opd', 'from'), sort: (row) => row.startsAt, cell: (row) => dateTime(row.startsAt) },
            { header: tr('opd', 'to'), sort: (row) => row.endsAt, cell: (row) => dateTime(row.endsAt) },
            { header: tr('opd', 'reason'), sort: (row) => row.reason, cell: (row) => <Badge variant="secondary">{humanize(row.reason)}</Badge> },
            { header: tr('opd', 'notes'), sort: (row) => row.notes, cell: (row) => row.notes ?? '—' },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                access.mayManageFor(row.doctorId) ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={tr('opd', 'remove_leave')}
                    title={tr('opd', 'remove_leave')}
                    onClick={() => setRemovingBlock(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null,
            },
          ]}
        />
      )}
      {removingBlock && (
        <ConfirmDialog
          title={tr('opd', 'remove_leave')}
          destructive
          confirmLabel={tr('opd', 'remove_leave')}
          onConfirm={() => removeBlock.mutateAsync(removingBlock)}
          onClose={() => setRemovingBlock(null)}
        />
      )}
      {removingHours && (
        <ConfirmDialog
          title={tr('opd', 'remove_clinic_hours')}
          description={`${removingHours.doctor.name} — ${removingHours.department?.name ?? tr('opd', 'all_departments')}`}
          destructive
          confirmLabel={tr('opd', 'remove_clinic_hours')}
          onConfirm={() => removeSchedule.mutateAsync(removingHours.id)}
          onClose={() => setRemovingHours(null)}
        >
          <p className="text-sm text-muted-foreground">{tr('opd', 'remove_clinic_hours_effect')}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
